import {
  AsyncComponent,
  AuthorizedRoute,
  OnlyPublicRoute,
} from '@wajeez-web/components/helpers';
import { applyTheme } from '@wajeez-web/core/utils';
import { useCommonSelector } from '@wajeez-web/stores/common';
import { ConfigProvider } from 'antd';
import { useEffect, useMemo } from 'react';

import {
  createBrowserRouter,
  LoaderFunction,
  matchPath,
  Outlet,
  RouteObject,
  RouterProvider,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import NavBar from '../NavBar/NavBar';
import SideBar from '../Sidebar/SideBar';
import { RouteLayoutProps } from './AuthorizedLayout';

import en from 'antd/es/locale/en_US';
import ar from 'antd/es/locale/ar_EG';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { SignInPage } from '@wajeez-web/modules/auth';
import { EndpointItem } from '@wajeez-web/core/types';
import {
  AuthActions,
  useAuthDispatch,
  useAuthSelector,
} from '@wajeez-web/stores/auth';

const loader: (pattern: string) => LoaderFunction =
  (pattern) => async (args) => {
    const path = getRelativeUrl(args.request.url);
    const match = matchPath(
      {
        path: pattern,
        caseSensitive: false,
        end: false,
      },
      path
    );

    return match?.params;
  };

// get url without the domain
const getRelativeUrl = (url: string) => {
  const urlObject = new URL(url);
  return urlObject.pathname;
};

const getRoutes = (routes: EndpointItem[], path: string): RouteObject[] => {
  return routes.map((item) =>
    item.type === 'container'
      ? {
          path: item.endpoint,
          element: <Outlet />,
          handle: item.crump
            ? {
                crumb: item.crump,
              }
            : undefined,
          children: getRoutes(item.children, path + item.endpoint + '/'),
          loader: item.crump ? loader(path + item.endpoint) : undefined,
        }
      : {
          index: item.endpoint === '/',
          path: item.endpoint,
          element: (
            <RouterHandler>
              <item.component />
            </RouterHandler>
          ),
          handle: item.crump
            ? {
                crumb: item.crump,
              }
            : undefined,
          loader: item.crump ? loader(path + item.endpoint) : undefined,
        }
  );
};

const MainApp = ({
  endpoints,
  routes,
  withSettings = true,
}: RouteLayoutProps & { withSettings?: boolean }) => {
  const { dir, lang } = useCommonSelector((state) => state.common.settings);
  const { i18n } = useTranslation();
  useEffect(() => {
    i18n.changeLanguage(lang);
    moment.locale('ar');
  }, [lang]);
  useEffect(() => {
    applyTheme('base');

    return () => {
      return;
    };
  }, []);

  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          path: '/auth',
          element: <OnlyPublicRoute />,
          children: [
            {
              path: 'login',
              element: <Outlet />,
              children: [
                {
                  index: true,
                  element: <SignInPage />,
                },
              ],
            },
          ],
        },
        {
          path: 'login',
          element: <Outlet />,
          children: [
            {
              element: <OnlyPublicRoute />,
              children: [
                {
                  index: true,
                  element: <SignInPage />,
                },
              ],
            },
          ],
        },
        {
          path: '/',
          element: <Outlet />,
          children: [
            {
              element: <AuthorizedRoute />,
              children: [
                {
                  element: (
                    <RouterHandler>
                      <div className="h-screen flex overflow-hidden bg-white">
                        <SideBar withSettings={withSettings} />
                        <div className="flex flex-col w-0 flex-1 overflow-hidden">
                          <NavBar />
                          <main className="flex-1 flex-grow overflow-y-auto focus:outline-none  h-full">
                            <div className=" h-full ">
                              <AsyncComponent>
                                <Outlet />
                              </AsyncComponent>
                            </div>
                          </main>
                        </div>
                      </div>
                    </RouterHandler>
                  ),
                  children: [
                    {
                      index: true,
                      element: <div></div>,
                    },
                    ...getRoutes(endpoints, '/'),
                  ],
                },
              ],
            },
          ],
        },
        {
          path: '*',
          element: <div>not found</div>,
        },
      ]),
    [endpoints]
  );

  return (
    <div dir={dir}>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#1f7983',
          },
        }}
        locale={lang === 'ar' ? ar : en}
        direction={dir}
      >
        <RouterProvider router={router} />
      </ConfigProvider>
    </div>
  );
};

export const RouterHandler = ({
  children,
}: // endpoint,
{
  children: JSX.Element;
  // endpoint?: EndpointItem | undefined;
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const authDispatch = useAuthDispatch();

  const initURL = useAuthSelector((state) => state.auth.initURL);
  const permissions = useAuthSelector((state) => state.auth.permission);
  const endpoints = useCommonSelector(({ navigation }) => navigation.endpoints);

  useEffect(() => {
    let hasPermission = false;

    const endpoint = endpoints.find((e) =>
      location.pathname.includes(`/${e.endpoint}`)
    );

    if (permissions && endpoint) {
      if (endpoint.per.length === 0) {
        hasPermission = true;
      } else
        for (const permission of endpoint.per) {
          hasPermission = permissions.includes(permission) || false;

          if (hasPermission) {
            break;
          }
        }
    }

    if (!hasPermission || !endpoint) {
      const url = endpoints.find((e) =>
        e.per.find((p) => permissions?.includes(p))
      )?.endpoint;

      if (url) {
        navigate(`/${url}`);
      }
    }
  }, [
    initURL,
    endpoints.length,
    permissions?.length,
    authDispatch,
    navigate,
    location,
  ]);

  return children;
};

export default MainApp;
